// import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useState, useMemo, useContext, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { VolumeResponse, Volume } from '~/shared/types/api';
import { SelectOption, Account } from '~/shared/utils/types';
import { DatafySwitchContext } from '~/shared/context/DatafySwitchProvider';
import { generateSkeletonRows, createSkeletonVolume } from '~/shared/utils/skeletonUtils';
import Table from '~/shared/components/Table';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { isOrganizationView } from '~/shared/utils/accountUtils';

import TagsTooltip from '../../../common/TagsTooltip';
import { canBeDatafied } from '../../helpers';
import { getVolumeStableRowId } from '~/shared/utils/volumeHelpers';

import { AllVolumesTableHeader } from './AllVolumesTableHeader';
import { useAppliedFilters } from './hooks/useAppliedFilters';
import { useResponsiveHeaderHeight } from './hooks/useResponsiveHeaderHeight';
import { getColumnDefs } from './columnDefs';
import { usePermission } from '~/shared/hooks/usePermission';
import { useFilterUrlParams } from '../AllVolumesFiltersSidePanel/hooks/useFilterUrlParams';

export const DATA_LIMIT = 50;

type VolumeWithSkeleton = Volume & { _isSkeleton?: boolean };

const sortKey = 'sort_field';
// const headerHeight = '88px';
// const navWidth = '4rem';

function AllVolumesTable({
  setPageOffset,
  pageOffset,
  sortField,
  data,
  loading = false,
  accounts: filterAccounts,
}: {
  setPageOffset: React.Dispatch<React.SetStateAction<string>>;
  pageOffset: string;
  sortField: { field: string; dir: 'desc' | 'asc' };
  data:
    | ({ volumes: VolumeResponse['volumes']; count: number } & Partial<VolumeResponse>)
    | undefined;
  loading?: boolean;
  accounts: Account[];
}) {
  const [_searchParams, setSearchParams] = useSearchParams();
  const { datafySwitchVolumes } = useContext(DatafySwitchContext);
  const { accounts, activeTenantId } = useAuthContext();
  const canWrite = usePermission('datafy.write.autoscaling');

  const [selectedVolumes, setSelectedVolumes] = useState<string[]>([]);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [dataCount, setDataCount] = useState<number>();

  const {
    appliedClusterNameFilters,
    appliedInstanceFilters,
    appliedVolumeIdFilters,
    appliedVolumeTagFilters,
    appliedAccountFilters,
    appliedAutoscaleState,
    activeFilterCount,
    currentBaseFilters,
  } = useAppliedFilters();

  const { resetFilters } = useFilterUrlParams();

  const { headerHeight, windowWidth } = useResponsiveHeaderHeight();
  const isOrgView = isOrganizationView(activeTenantId, accounts);

  useEffect(() => {
    if (data?.count !== undefined) {
      setDataCount(data.count);
    }
  }, [data?.count]);

  useEffect(() => {
    if (!dataCount) {
      return;
    }
    const totalPages = Math.ceil(dataCount / DATA_LIMIT) || 1;
    const currentPageIdx = Math.floor((parseInt(pageOffset, 10) || 0) / DATA_LIMIT);
    if (currentPageIdx > totalPages - 1) {
      setPageOffset(`${(totalPages - 1) * DATA_LIMIT}`);
    }
  }, [dataCount, pageOffset, setPageOffset]);

  const toggleSidePanel = useCallback(() => setSidePanelOpen((o) => !o), []);
  const setPanelOpen = useCallback((open: boolean) => setSidePanelOpen(open), []);

  const rowData = useMemo(() => {
    if (loading && (!data?.volumes || data.volumes.length === 0)) {
      return generateSkeletonRows(DATA_LIMIT, createSkeletonVolume()) as VolumeWithSkeleton[];
    }
    return data?.volumes || [];
  }, [data?.volumes, loading]);

  const columnDefs = useMemo(
    () =>
      getColumnDefs({
        onSelectAllRows: () =>
          setSelectedVolumes((prev) =>
            prev?.length > 0
              ? []
              : data?.volumes.filter(canBeDatafied).map((v) => v.volumeId!) || []
          ),
        sortField,
        selectedVolumes,
        setSelectedVolumes,
        datafySwitchVolumes,
        windowWidth,
        canWrite,
        isOrgView,
        accounts,
      }),
    [
      sortField,
      datafySwitchVolumes,
      windowWidth,
      selectedVolumes,
      data?.volumes,
      isOrgView,
      accounts,
      canWrite,
    ]
  );

  const onPageChange = (page: number): void => {
    setSelectedVolumes([]);
    setPageOffset(`${page * DATA_LIMIT}`);
  };

  const handleResetFilters = useCallback(() => {
    setPageOffset('0');
    resetFilters();
    setSidePanelOpen(false);
  }, [resetFilters, setPageOffset]);

  // const isSkeletonLoading = loading && (!data?.volumes || data.volumes.length === 0);

  return (
    <div className={'flex flex-col min-h-[348px] h-full gap-2.5 2xl:gap-3.5'}>
      <AllVolumesTableHeader
        dataCount={loading ? undefined : dataCount}
        activeFilterCount={activeFilterCount}
        sidePanelOpen={sidePanelOpen}
        onTogglePanel={toggleSidePanel}
        onSetPanelOpen={setPanelOpen}
        onResetFilters={handleResetFilters}
        appliedClusterNameFilters={appliedClusterNameFilters as SelectOption[]}
        appliedInstanceFilters={appliedInstanceFilters as SelectOption[]}
        appliedVolumeIdFilters={appliedVolumeIdFilters as SelectOption[]}
        appliedVolumeTagFilters={appliedVolumeTagFilters as SelectOption[]}
        appliedAccountFilters={appliedAccountFilters as SelectOption[]}
        appliedAutoscaleState={appliedAutoscaleState}
        isOrgView={isOrgView}
        accounts={filterAccounts}
        activeOrgId={activeTenantId}
        activeFilters={currentBaseFilters}
        sortField={sortField}
      />
      <Table
        rowData={rowData}
        columnDefs={columnDefs}
        headerHeight={headerHeight.height}
        loading={false}
        disableClientSort
        suppressNoRowsWhileLoading={true}
        itemsName={'volumes'}
        getRowId={(params) => {
          // Handle skeleton rows with unique IDs
          const data = params.data as VolumeWithSkeleton;
          if (data && '_isSkeleton' in data && data._isSkeleton) {
            return getVolumeStableRowId(data) || 'skeleton';
          }
          return getVolumeStableRowId(data);
        }}
        pagination={{
          perPage: DATA_LIMIT,
          onChange: onPageChange,
          totalPages: Math.ceil((dataCount || DATA_LIMIT) / DATA_LIMIT),
          dataCount: dataCount || 0,
          itemsName: 'volumes',
          currentPage: Math.floor((parseInt(pageOffset, 10) || 0) / DATA_LIMIT),
        }}
        onSortChange={(sort) => {
          // Prevent sorting when loading skeleton data
          if (loading && (!data?.volumes || data.volumes.length === 0)) {
            return;
          }
          setSearchParams(
            (params) => {
              params.set(sortKey, JSON.stringify(sort));
              return params;
            },
            { replace: true }
          );
        }}
      />

      {/* Only render TagsTooltip for non-skeleton data */}
      {!loading &&
        rowData
          ?.filter((data) => {
            const skelData = data as VolumeWithSkeleton;
            return !('_isSkeleton' in skelData && skelData._isSkeleton);
          })
          ?.map((data) => <TagsTooltip key={getVolumeStableRowId(data as Volume)} {...data} />)}
    </div>
  );
}

export default AllVolumesTable;
